import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { authService } from '../services/authService';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  const profileOperations = {
    async load(userId) {
      if (!userId) return;
      setProfileLoading(true);
      try {
        const { data, error } = await authService?.getProfile(userId);
        if (!error && data) {
          setUserProfile(data);
        }
      } catch (error) {
        console.error('Profile load error:', error);
      } finally {
        setProfileLoading(false);
      }
    },

    clear() {
      setUserProfile(null);
      setProfileLoading(false);
    }
  };

  const authStateHandlers = {
    onChange: (event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id);
      } else {
        profileOperations?.clear();
      }
    }
  };

  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session);
    });

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    );

    return () => subscription?.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    return await authService?.signIn(email, password);
  };

  const signUp = async (email, password, profileData) => {
    return await authService?.signUp(email, password, profileData);
  };

  const resetPassword = async (email) => {
    return await authService?.resetPassword(email);
  };

  const resendConfirmationEmail = async (email) => {
    return await authService?.resendConfirmationEmail(email);
  };

  const signOut = async () => {
    const result = await authService?.signOut();
    if (!result?.error) {
      setUser(null);
      profileOperations?.clear();
    }
    return result;
  };

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } };
    
    const result = await authService?.updateProfile(user?.id, updates);
    if (!result?.error && result?.data) {
      setUserProfile(result?.data);
    }
    return result;
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    resetPassword,
    resendConfirmationEmail,
    isAuthenticated: !!user,
    emailVerified: user?.email_confirmed_at ? true : (userProfile?.email_verified ?? false)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
export { AuthContext };